import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import rankingService from "./rankingService";
import { setAlertError, setAlertSuccess } from "../alert/alertSlice";

const initialState = {
  districts: [],
  events: [],
  playerRanking: null,
  rankings: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
};

export const getDistricts = createAsyncThunk(
  "ranking/getDistricts",
  async (_, thunkAPI) => {
    try {
      const response = await rankingService.getDistricts();
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to fetch districts";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const getEvents = createAsyncThunk(
  "ranking/getEvents",
  async (_, thunkAPI) => {
    try {
      const response = await rankingService.getEvents();
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to fetch events";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const getPlayerRankingByBsa = createAsyncThunk(
  "ranking/getPlayerRankingByBsa",
  async (bsaNumber, thunkAPI) => {
    try {
      const response = await rankingService.getPlayerRankingByBsa(bsaNumber);
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message ||
        error.message ||
        "Failed to fetch player ranking by BSA number";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const getRankings = createAsyncThunk(
  "ranking/getRankings",
  async (params, thunkAPI) => {
    try {
      const response = await rankingService.getRankings(params);
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to fetch rankings";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

const rankingSlice = createSlice({
  name: "ranking",
  initialState,
  reducers: {
    reset: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDistricts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDistricts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.districts = action.payload;
      })
      .addCase(getDistricts.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload?.message || "Error fetching districts";
      })
      .addCase(getEvents.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getEvents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.events = action.payload;
      })
      .addCase(getEvents.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload?.message || "Error fetching events";
      })
      .addCase(getPlayerRankingByBsa.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPlayerRankingByBsa.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.playerRanking = action.payload;
      })
      .addCase(getPlayerRankingByBsa.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message =
          action.payload?.message ||
          "Error fetching player ranking by BSA number";
      })
      .addCase(getRankings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRankings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.rankings = action.payload.data.rankings;
      })
      .addCase(getRankings.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload?.message || "Error fetching rankings";
      });
  },
});

export const { reset } = rankingSlice.actions;
export default rankingSlice.reducer;
